import React, { useState, useMemo } from "react";
import { Modal, Row, Col, Table, Spinner, Placeholder } from "react-bootstrap";
import { ModalSkeleton } from "../../../components/Dashboard/SkeletonLoaders";
import moment from "moment";
import { X } from "lucide-react";
import "./LandlordDetailsModal.css";

const LandlordDetailsModal = ({
  showModal,
  selectedLandlord,
  landlordMetricsList,
  loadingDetails,
  onHide,
}) => {
  const [workspaceFilter, setWorkspaceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  // Get unique workspaces for the dropdown
  const uniqueWorkspaces = useMemo(() => {
    if (!landlordMetricsList) return [];
    const workspaces = landlordMetricsList.map(item => item.workspaceId).filter(Boolean);
    const uniqueIds = [...new Set(workspaces.map(ws => ws._id))];
    return uniqueIds.map(id => workspaces.find(ws => ws._id === id));
  }, [landlordMetricsList]);

  // Helper function to determine if an item is deleted
  const isItemDeleted = (item) => item.deleted === true || !item.tenant_id?.email && !item.tenant_id?.fname && !item.tenant_id?.lname;

  // Helper function to determine if an item has tenant checks
  const hasTenantChecks = (item) => item.tenant_check === true || item.tenant_checks === true || (typeof item.tenant_checks === 'number' && item.tenant_checks > 0);

  // Filtering logic
  const filteredList = useMemo(() => {
    if (!landlordMetricsList) return [];
    
    let filtered = landlordMetricsList;
    
    // Apply status filter
    if (statusFilter === "active") {
      filtered = filtered.filter(f => !isItemDeleted(f));
    } else if (statusFilter === "deleted") {
      filtered = filtered.filter(f => isItemDeleted(f));
    }
    
    // Apply type filter
    if (typeFilter === "profile") {
      filtered = filtered.filter(f => !hasTenantChecks(f));
    } else if (typeFilter === "check") {
      filtered = filtered.filter(f => hasTenantChecks(f));
    }
    
    // Apply workspace filter
    if (workspaceFilter !== "all") {
      filtered = filtered.filter(f => f.workspaceId?._id === workspaceFilter);
    }
    
    return filtered;
  }, [landlordMetricsList, workspaceFilter, statusFilter, typeFilter]);

  // Calculate metrics for summary cards
  const summaryData = useMemo(() => {
    if (!selectedLandlord) return { profiles: 0, deleted: 0, checks: 0, workspaces: [] };
    
    if (!landlordMetricsList || landlordMetricsList.length === 0) {
      return {
        profiles: selectedLandlord.tenantCount || 0,
        deleted: selectedLandlord.deletedTenantCount || 0,
        checks: selectedLandlord.tenantChecks || 0,
        workspaces: selectedLandlord.workspaces || []
      };
    }
    
    const profiles = filteredList.filter(f => !hasTenantChecks(f) && !isItemDeleted(f));
    const tenantChecks = filteredList.filter(f => hasTenantChecks(f) && !isItemDeleted(f));
    const deletedProfiles = filteredList.filter(f => !hasTenantChecks(f) && isItemDeleted(f));
    const deletedTenantChecks = filteredList.filter(f => hasTenantChecks(f) && isItemDeleted(f));
    const all = filteredList.filter(f => true);
    
    const workspacesMap = new Map();
    all.forEach(item => {
      if (item.workspaceId?._id) {
        workspacesMap.set(item.workspaceId._id, item.workspaceId);
      }
    });

    return {
      profiles: profiles.length,
      checks: tenantChecks.reduce((sum, item) => sum + (typeof item.tenant_checks === 'number' ? item.tenant_checks : 1), 0),
      deleted: deletedProfiles.length + deletedTenantChecks.length,
      deletedProfiles: deletedProfiles.length,
      deletedTenantChecks: deletedTenantChecks.length,
      workspaces: Array.from(workspacesMap.values())
    };
  }, [selectedLandlord, landlordMetricsList, filteredList]);

  // Calculate unfiltered counts for header card
  // const unfilteredCounts = useMemo(() => {
  //   if (!selectedLandlord) return { profiles: 0, checks: 0, deleted: 0 };

  //   if (!landlordMetricsList || landlordMetricsList.length === 0) {
  //     return {
  //       profiles: selectedLandlord.tenantCount || 0,
  //       checks: selectedLandlord.tenantChecks || 0,
  //       deleted: selectedLandlord.deletedTenantCount || 0
  //     };
  //   }
    
  //   const profiles = landlordMetricsList.filter(f => !hasTenantChecks(f) && !isItemDeleted(f));
  //   const tenantChecks = landlordMetricsList.filter(f => hasTenantChecks(f) && !isItemDeleted(f));
  //   const deletedProfiles = landlordMetricsList.filter(f => !hasTenantChecks(f) && isItemDeleted(f));
  //   const deletedTenantChecks = landlordMetricsList.filter(f => hasTenantChecks(f) && isItemDeleted(f));

  //   return {
  //     profiles: profiles.length,
  //     checks: tenantChecks.reduce((sum, item) => sum + (typeof item.tenant_checks === 'number' ? item.tenant_checks : 1), 0),
  //     deleted: deletedProfiles.length + deletedTenantChecks.length
  //   };
  // }, [selectedLandlord, landlordMetricsList]);

  // Find repeated tenants in the filtered list for highlighting
  const repeatedTenantIds = useMemo(() => {
    if (!filteredList) return new Set();
    const tenantCounts = new Map();
    filteredList.forEach(item => {
      if (item.tenant_id?._id) {
        tenantCounts.set(item.tenant_id._id, (tenantCounts.get(item.tenant_id._id) || 0) + 1);
      }
    });
    const repeatedIds = new Set();
    tenantCounts.forEach((count, tenantId) => {
      if (count > 1) {
        repeatedIds.add(tenantId);
      }
    });
    return repeatedIds;
  }, [filteredList]);

  return (
    <Modal 
      show={showModal} 
      onHide={onHide} 
      size="xl" 
      dialogClassName="modal-90w"
      centered 
      scrollable 
      className="landlord-metrics-modal"
    >
      <Modal.Header className="landlord-details-header">
        <div className="d-flex justify-content-between align-items-start w-100">
          <div className="w-100">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h4 className="landlord-details-name">
                  Landlord {selectedLandlord?.landlord?.fname_companyName} {selectedLandlord?.landlord?.lname}
                </h4>
                <p className="landlord-details-email">
                  {selectedLandlord?.landlord?.email || "No email provided"}
                </p>
              </div>
              <button className="modal-close-btn" onClick={onHide}>
                <X size={20} />
              </button>
            </div>
            {/* <div className="landlord-counts-badges mt-3 d-flex gap-3">
              <span className="landlord-count-badge">
                <strong>{unfilteredCounts.profiles}</strong> Profiles
              </span>
              <span className="landlord-count-badge">
                <strong>{unfilteredCounts.checks}</strong> Checks
              </span>
              <span className="landlord-count-badge text-deleted">
                <strong>{unfilteredCounts.deleted}</strong> Deleted
              </span>
            </div> */}
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="px-5 pb-5 pt-0">
        {loadingDetails ? (
          <ModalSkeleton />
        ) : selectedLandlord && (
          <>
          {/* Filter Section */}
            <div className="modal-filter-section mb-4">
                <div className="workspace-select-container">
                  <label className="filter-pill-label mb-2 d-block">Workspace</label>
                  <select 
                    className="workspace-select"
                    value={workspaceFilter}
                    onChange={(e) => setWorkspaceFilter(e.target.value)}
                  >
                    <option value="all">All workspaces</option>
                    {uniqueWorkspaces.map(ws => (
                      <option key={ws._id} value={ws._id}>{ws.name}</option>
                    ))}
                  </select>
                </div>

              <div className="filter-pills-row mt-3">
                <div className="filter-pill-group">
                  <span className="filter-pill-label">Status</span>
                  <div className="filter-pill-options">
                    {['all', 'active', 'deleted'].map(s => (
                      <div 
                        key={s}
                        className={`modal-filter-pill ${statusFilter === s ? 'active' : ''}`}
                        onClick={() => setStatusFilter(s)}
                      >
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="filter-pill-group">
                  <span className="filter-pill-label">Type</span>
                  <div className="filter-pill-options">
                    {['all', 'profile', 'check'].map(t => (
                      <div 
                        key={t}
                        className={`modal-filter-pill ${typeFilter === t ? 'active' : ''}`}
                        onClick={() => setTypeFilter(t)}
                      >
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Summary Cards */}
            <Row className="g-4 mb-5">
              <Col md={4}>
                <div className="landlord-summary-card">
                  <div className="landlord-summary-label">Total Profiles</div>
                  <div className="landlord-summary-value">{(Number(summaryData.profiles) || 0) +
        (Number(summaryData.deletedProfiles) || 0)}</div>
                </div>
              </Col>
              <Col md={4}>
                <div className="landlord-summary-card">
                  <div className="landlord-summary-label">Total Tenant Checks</div>
                  <div className="landlord-summary-value">{(Number(summaryData.checks) || 0) +
        (Number(summaryData.deletedTenantChecks) || 0)}</div>
                </div>
              </Col>
              <Col md={4}>
                <div className="landlord-summary-card">
                  <div className="landlord-summary-label">Workspaces</div>
                  <div className="landlord-summary-value">{summaryData.workspaces.length}</div>
                  <div className="landlord-summary-sub text-muted" title={summaryData.workspaces.map(ws => ws.name).join(', ')}>
                    {summaryData.workspaces.map(ws => ws.name).join(', ')}
                  </div>
                </div>
              </Col>
            </Row>


            {/* Table Section */}
            <div className="text-muted small mb-3">
              This table below does not filter duplicates • duplicate entries are highlighted in yellow
            </div>
            <div className="table-responsive">
              <Table hover className="landlord-modal-table mb-0">
                <thead>
                  <tr>
                    <th className="text-center">#</th>
                    <th>TENANT NAME</th>
                    <th>EMAIL</th>
                    <th>WORKSPACE</th>
                    <th>JOINED DATE</th>
                    <th>TYPE</th>
                    <th>STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredList.length > 0 ? (
                    filteredList.map((item, index) => {
                      const isDeleted = item.deleted === true || !item.tenant_id?.email && !item.tenant_id?.fname && !item.tenant_id?.lname;
                      const isRepeated = item.tenant_id?._id && repeatedTenantIds.has(item.tenant_id._id);
                       return (
                         <tr key={item._id} className={isRepeated ? "tenant-repeated" : ""}>
                          <td className="text-center text-muted small">
                            {index + 1}
                          </td>
                          <td className="fw-700">
                            {item.tenant_id?.fname} {item.tenant_id?.lname}
                          </td>
                          <td className="text-muted small">
                            {item.tenant_id?.email || "N/A"}
                          </td>
                          <td className="fw-600">
                            {item.workspaceId?.name || "N/A"}
                          </td>
                          <td className="text-muted small fw-medium">
                            {item.created_at
                              ? moment(item.created_at).format("DD MMM YYYY, HH:mm")
                              : "N/A"}
                          </td>
                          <td>
                            {(item.tenant_check || item.tenant_checks) ? (
                              <span className="type-badge-check">Check</span>
                            ) : (
                              <span className="type-badge-profile">Profile</span>
                            )}
                          </td>
                          <td>
                            <span className={isDeleted ? "status-badge-deleted" : "status-badge-active"}>
                              {isDeleted ? "Deleted" : "Active"}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-5 text-muted">
                        No results found matching your filters
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default LandlordDetailsModal;